import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Eye, EyeOff, Lock } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChangePasswordPage = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      toast.success('Password changed successfully!');
      if (refreshUser) {
        await refreshUser();
        await new Promise(r => setTimeout(r, 100));
      }
      navigate('/', { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zom-black flex flex-col items-center justify-center p-4">
      {/* Header */}
      <div className="mb-8 text-center">
        <div className="w-16 h-16 bg-zom-red border-3 border-zom-yellow flex items-center justify-center mx-auto mb-4">
          <Lock className="w-8 h-8 text-zom-yellow" strokeWidth={2.5} />
        </div>
        <h1 className="font-heading text-2xl text-zom-yellow tracking-wider">
          ZMIEŃ HASŁO
        </h1>
        <p className="text-zom-white/60 mt-2 text-sm font-body">
          {user?.must_change_password 
            ? 'Musisz zmienić hasło przed kontynuowaniem'
            : 'Zaktualizuj swoje hasło'
          }
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-4">
        <div className="relative">
          <input
            type={showCurrent ? 'text' : 'password'}
            placeholder="OBECNE HASŁO"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            className="w-full bg-zom-white border-3 border-zom-black text-zom-black placeholder:text-zom-black/40 h-14 px-4 pr-14 font-heading text-sm tracking-wide"
            data-testid="current-password-input"
          />
          <button
            type="button"
            onClick={() => setShowCurrent(!showCurrent)}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-zom-black hover:text-zom-red transition-colors"
          >
            {showCurrent ? <EyeOff className="w-5 h-5" strokeWidth={2.5} /> : <Eye className="w-5 h-5" strokeWidth={2.5} />}
          </button>
        </div>

        <div className="relative">
          <input
            type={showNew ? 'text' : 'password'}
            placeholder="NOWE HASŁO"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="w-full bg-zom-white border-3 border-zom-black text-zom-black placeholder:text-zom-black/40 h-14 px-4 pr-14 font-heading text-sm tracking-wide"
            data-testid="new-password-input"
          />
          <button
            type="button"
            onClick={() => setShowNew(!showNew)}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-zom-black hover:text-zom-red transition-colors"
          >
            {showNew ? <EyeOff className="w-5 h-5" strokeWidth={2.5} /> : <Eye className="w-5 h-5" strokeWidth={2.5} />}
          </button>
        </div>

        <div>
          <input
            type={showNew ? 'text' : 'password'}
            placeholder="POTWIERDŹ NOWE HASŁO"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="w-full bg-zom-white border-3 border-zom-black text-zom-black placeholder:text-zom-black/40 h-14 px-4 font-heading text-sm tracking-wide"
            data-testid="confirm-password-input"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full h-14 border-3 border-zom-yellow bg-zom-yellow text-zom-black font-heading text-lg tracking-widest hover:bg-zom-red hover:border-zom-red hover:text-zom-white transition-all disabled:opacity-50"
          data-testid="change-password-btn"
        >
          {loading ? (
            <div className="w-6 h-6 border-3 border-current border-t-transparent animate-spin mx-auto" />
          ) : (
            'ZMIEŃ HASŁO'
          )}
        </button>
      </form>

      <p className="text-zom-white/40 text-xs mt-8 text-center font-body">
        Hasło musi mieć co najmniej 6 znaków
      </p>
    </div>
  );
};

export default ChangePasswordPage;
